import { createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import {
    deleteContract as deleteContractApi,
    deleteContractFile as deleteContractFileApi,
    deleteContracts as deleteContractsApi,
    getContract as getContractApi,
    getContractFile as getContractFileApi,
    getContractOptions as getContractOptionsApi,
    getContracts as getContractsApi,
    contractForm as updateContractApi,
    updateContractFile as updateContractFileApi,
} from '../../api/contracts';
import { CONTRACT_FILE_TYPES } from '../../components/dashboard/contracts/contract/contractFileTypesEnum';

const toDateOrNull = (date) => (date ? new Date(date) : null);

const normalizeContractDates = (contract) => ({
    ...(contract || {}),
    startDate: toDateOrNull(contract?.startDate),
    endDate: toDateOrNull(contract?.endDate),
    renewalDate: toDateOrNull(contract?.renewalDate),
    terminationEffectiveDate: toDateOrNull(
        contract?.terminationEffectiveDate,
    ),
});

const initialState = {
    isLoading: false,
    error: null,
    contracts: [],
    hasLoadedContracts: false,
    contractById: {},
    contractOptions: {},
};

const slice = createSlice({
    name: 'contracts',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
            enqueueSnackbar(action.payload.errorMessage || action.payload.error?.message, { variant: 'error' });
        },
        getContractsSuccess(state, action) {
            state.isLoading = false;
            state.contracts = (action.payload || []).map(normalizeContractDates);
            state.hasLoadedContracts = true;
        },
        getContractSuccess(state, action) {
            state.isLoading = false;
            const contract = normalizeContractDates(action.payload);
            const id = contract?.id;
            if (id !== undefined && id !== null) {
                state.contractById[id] = contract;
            }
        },
        getContractOptionsSuccess(state, action) {
            state.isLoading = false;
            const { facilities, contractVendors, contractTypes } = action.payload;
            state.contractOptions.facilities = facilities;
            state.contractOptions.contractVendors = contractVendors;
            state.contractOptions.contractTypes = contractTypes;
        },

        getContractFileSuccess(state, action) {
            const { file, fileType, id } = action.payload;
            state.isLoading = false;
            if (!state.contractById[id]) return;
            if (fileType === CONTRACT_FILE_TYPES.CONTRACT_LETTER) {
                state.contractById[id].contractFile = file;
            } else if (fileType === CONTRACT_FILE_TYPES.TERMINATION_LETTER) {
                state.contractById[id].terminationFile = file;
            }
        },
        upsertContractSuccess(state, action) {
            state.isLoading = false;
            const contract = normalizeContractDates(action.payload);
            const id = contract?.id;
            if (id === undefined || id === null) {
                return;
            }

            const existingListIndex = state.contracts.findIndex((item) => item?.id === id);
            if (existingListIndex > -1) {
                state.contracts[existingListIndex] = {
                    ...state.contracts[existingListIndex],
                    ...contract,
                };
            } else {
                state.contracts.push(contract);
            }

            state.contractById[id] = {
                ...state.contractById[id],
                ...contract,
            };
        },
        updateContractFileSuccess(state, action) {
            state.isLoading = false;
            const { id, result } = action.payload;
            state.contractById[result.id] = { ...state.contractById[result.id], ...result };
            const index = state.contracts.findIndex((item) => item?.id === result.id);
            if (index > -1) {
                state.contracts[index] = { ...state.contracts[index], ...result };
            } else if (id === 0) {
                state.contracts.push(result)
            }
        },
        deleteContractFileSuccess(state, action) {
            state.isLoading = false;
            const { id, fileType } = action.payload;
            const fileTypeField = fileType === CONTRACT_FILE_TYPES.CONTRACT_LETTER ? 'contract' : 'termination';
            const clearFile = (contract) => {
                if (!contract) return;
                contract[`${fileTypeField}File`] = null;
                contract[`${fileTypeField}LetterFileName`] = null;
                contract[`${fileTypeField}LetterExtension`] = null;
            };
            clearFile(state.contractById[id]);
            clearFile(state.contracts.find((contract) => Number(contract?.id) === Number(id)));
        },
        deleteContractSuccess(state, action) {
            state.isLoading = false;
            const id = action.payload;
            state.contracts = state.contracts.filter((item) => item?.id !== id);
            if (state.contractById?.[id]) {
                delete state.contractById[id];
            }
        },
        deleteContractsSuccess(state, action) {
            state.isLoading = false;
            const ids = action.payload;
            state.contracts = state.contracts.filter((item) => !ids.includes(item?.id));
            ids.forEach((contractId) => {
                if (state.contractById?.[contractId]) {
                    delete state.contractById[contractId];
                }
            });
        },
    },
});

export default slice.reducer;

export function getContracts() {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await getContractsApi();
            dispatch(slice.actions.getContractsSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load contracts' }));
        }
    };
}

export function getContractOptions() {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await getContractOptionsApi();
            dispatch(slice.actions.getContractOptionsSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load contract options' }));
        }
    };
}

export function getContract(id) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await getContractApi(id);
            if (!response.data?.success) {
                throw new Error(response?.data?.message);
            }
            dispatch(slice.actions.getContractSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: error?.message || 'Failed to load contract' }));
        }
    };
}

export function getContractFile(id, fileType) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await getContractFileApi(id, fileType);
            if (!response?.data) {
                throw new Error();
            }
            dispatch(slice.actions.getContractFileSuccess({ file: response.data, fileType, id }));
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: error?.message || `Failed to load ${fileType === CONTRACT_FILE_TYPES.TERMINATION_LETTER ? 'termination' : 'contract'} file` }));
        }
    };
}

export function updateContract(form) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await updateContractApi(form);
            if (!response.data?.success) {
                throw new Error(response?.data?.message);
            }
            dispatch(slice.actions.upsertContractSuccess(response.data?.data));
            enqueueSnackbar(response.data.message, { variant: 'success' });
            return response.data;
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: error?.message || 'Failed to update contract' }));
        }
        return null;
    };
}

export function updateContractFile(file, id, fileType) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await updateContractFileApi(file, id, fileType);
            if (!response.data?.success) {
                throw new Error(response?.data?.message);
            }
            dispatch(slice.actions.updateContractFileSuccess({ id, result: response.data.data }));
            enqueueSnackbar('File added successfully', { variant: 'success' });
            return response.data.data;
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: error?.message || 'Failed to upload file' }));
            throw error;
        }
    }
}

export function deleteContract(id) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await deleteContractApi(id);
            if (!response?.data?.success) {
                throw new Error(response?.data?.message);
            }
            dispatch(slice.actions.deleteContractSuccess(id));
            enqueueSnackbar(response.data.message, { variant: 'success' });
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: error?.message || 'Failed to delete contract' }));
        }
        return null;
    };
}

export function deleteContracts(ids) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await deleteContractsApi(ids);
            if (!response?.data?.success) {
                throw new Error(response?.data?.message);
            }
            dispatch(slice.actions.deleteContractsSuccess(ids));
            enqueueSnackbar(response.data.message, { variant: 'success' });
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: error?.message || 'Failed to delete contracts' }));
        }
    };
}
export function deleteContractFile(id, fileType) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await deleteContractFileApi(id, fileType);
            if (!response?.data?.success) {
                throw new Error(response?.data?.message);
            }
            dispatch(slice.actions.deleteContractFileSuccess({ id, fileType }));
            enqueueSnackbar(response.data.message, { variant: 'success' });
        } catch (error) {
            dispatch(slice.actions.hasError({ error, errorMessage: error?.message || 'Failed to delete file' }));
            throw error;
        }
    };
}

